import { Injectable } from "@angular/core";
import * as signalr from "@microsoft/signalr";
import { IRetryPolicy } from "@microsoft/signalr";
import { RetryContext } from "@microsoft/signalr/src/IRetryPolicy";
import { AppVersionHubSubscription } from "@main/app-versioning/app-versioning.models";


@Injectable()
export class AppVersionHub {
    public readonly url: string = "/api/app-versioning/app-version-hub";

    public getConnection(): signalr.HubConnection {
        return new signalr.HubConnectionBuilder().withUrl(this.url)
            .withAutomaticReconnect(new AppVersionHubRetryPolicy()).build();
    }

    public startConnection(connection: signalr.HubConnection): void {
        connection.start().catch(error => {
            const context: string = "Error while starting connection";
            const message: string = error?.message ?? error?.error ?? error;
            // eslint-disable-next-line no-console
            console.error(!!message?.length ? `${context}: ${message}` : context);
        });
    }

    public subscribeEvents(connection: signalr.HubConnection, subscriptions: AppVersionHubSubscription[]): void {
        subscriptions?.forEach((subscription: AppVersionHubSubscription): void => {
            connection.on(subscription.event, subscription.handler);

            const context: string = `A subscription was made to ${subscription.event} event of ${AppVersionHub.name}`;
            // eslint-disable-next-line no-console
            console.info(!!subscription.listener?.length ? `${context} in ${subscription.listener}` : context);
        });
    }

    public getListeningConnection(subscriptions?: AppVersionHubSubscription[]): signalr.HubConnection {
        const connection: signalr.HubConnection = this.getConnection();
        this.subscribeEvents(connection, subscriptions);
        this.startConnection(connection);
        return connection;
    }

    public releaseConnection(connection: signalr.HubConnection): void {
        connection?.stop().then();
    }
}

class AppVersionHubRetryPolicy implements IRetryPolicy {
    public readonly maxRetryCount: number = 10;

    public nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
        // Stop retrying after 10 attempts.
        if (retryContext.previousRetryCount >= this.maxRetryCount) return null;

        // Make an exponential backoff.
        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
    }
}